// The admin Models page's data (TOM-465, ADR 0322): the shapes
// GET /v1/admin/models and its siblings answer with (src/core/llm/model-admin.ts),
// the calls, and the wording helpers the page and its tests share.

import { adminApi } from './adminApi';

export type ModelProvider = 'anthropic' | 'openrouter';
export type ModelRole =
  | 'neo_chat'
  | 'neo_changes'
  | 'neo_boards'
  | 'neo_electrical'
  | 'neo_mechanical'
  | 'neo_electronics'
  | 'neo_navigation'
  | 'neo_pictures'
  | 'watch_screen'
  | 'watch_review'
  | 'equipment_analysis'
  | 'report_narration'
  | 'neo_recall_summary'
  | 'neo_profile_consolidation'
  | 'eval_judges'
  | 'neo_trainer';

export interface ModelPrices {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
}

export interface ModelView {
  id: string;
  provider: ModelProvider;
  modelId: string;
  label: string;
  configured: boolean;
  listing: 'listed' | 'delisted' | 'unknown';
  delistedAt: string | null;
  contextTokens: number | null;
  maxOutputTokens: number | null;
  capabilities: Record<string, boolean> | null;
  prices:
    | (ModelPrices & { longPrompt: (ModelPrices & { overPromptTokens: number }) | null })
    | null;
  priceSource: 'anthropic' | 'openrouter' | 'built_in' | 'ceiling' | null;
  /**
   * Calls to this model carry no privacy rule (TOM-657, ADR 0389): GPT Image 2,
   * which draws on OpenRouter's images endpoint, where no privacy setting can
   * be sent.
   */
  noPrivacyRule: boolean;
}

export type ModelSource = 'admin' | 'deploy_variable' | 'built_in' | 'follows';

/** What a role's call needs from a model: `image_output` is Neo's pictures (TOM-641). */
export type ModelRoleNeed = 'tools' | 'structured_output' | 'image_output';

export interface ModelRoleView {
  role: ModelRole;
  label: string;
  description: string;
  needs: ModelRoleNeed[];
  maxModels: number;
  models: ModelView[];
  source: ModelSource;
  envVar: string | null;
  deployValue: string | null;
  builtIn: string[] | null;
  follows: ModelRole | null;
  /** What the role runs on when nothing is set here: what a reset goes back to. */
  fallback: { models: ModelView[]; source: Exclude<ModelSource, 'admin'> };
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface ModelChoiceView {
  alias: string;
  label: string;
  graphics: boolean;
  vision: boolean;
  model: ModelView;
}

export interface CatalogSourceView {
  source: 'anthropic_models' | 'anthropic_prices' | 'openrouter_models';
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  modelCount: number | null;
}

/** One expert behind Neo, its role here, and whether owners get it (TOM-139). */
export interface SpecialistView {
  id: string;
  role: ModelRole;
  live: boolean;
}

export interface ModelsView {
  roles: ModelRoleView[];
  /** Every expert defined, live or not, from the server's registry (TOM-139). */
  specialists: SpecialistView[];
  choices: ModelChoiceView[];
  choicesSource: 'admin' | 'built_in';
  choicesUpdatedAt: string | null;
  choicesUpdatedBy: string | null;
  catalog: { sources: CatalogSourceView[]; counts: Record<ModelProvider, number> };
  keys: Record<ModelProvider, boolean>;
}

export interface ModelCheckStep {
  name: 'tools' | 'structured_output' | 'text' | 'image_output';
  ok: boolean;
  detail: string | null;
  latencyMs: number;
  /** What the step cost in USD, when known: the picture step's (TOM-641). */
  costUsd?: number | null;
}

export interface ModelCheckResult {
  role: ModelRole;
  model: string;
  ok: boolean;
  refused: 'invalid_model' | 'not_configured' | null;
  steps: ModelCheckStep[];
}

export interface ChoiceDraft {
  alias: string;
  model: string;
  label: string;
  graphics: boolean;
  vision: boolean;
}

// ----- Calls ---------------------------------------------------------------------

export const modelsApi = {
  view(): Promise<ModelsView> {
    return adminApi<ModelsView>('/v1/admin/models');
  },
  catalog(role: ModelRole, q: string): Promise<{ models: ModelView[]; total: number }> {
    const params = new URLSearchParams({ role });
    if (q.trim().length > 0) params.set('q', q.trim());
    return adminApi(`/v1/admin/models/catalog?${params.toString()}`);
  },
  /** The whole catalog for the catalog view, models no longer listed included. */
  catalogAll(): Promise<{ models: ModelView[]; total: number }> {
    return adminApi('/v1/admin/models/catalog?delisted=1&limit=1000');
  },
  check(role: ModelRole, model: string): Promise<ModelCheckResult> {
    return adminApi<ModelCheckResult>('/v1/admin/models/check', {
      method: 'POST',
      body: { role, model },
    });
  },
  setRole(role: ModelRole, models: string[]): Promise<ModelsView> {
    return adminApi<ModelsView>(`/v1/admin/models/roles/${role}`, {
      method: 'PUT',
      body: { models },
    });
  },
  resetRole(role: ModelRole): Promise<ModelsView> {
    return adminApi<ModelsView>(`/v1/admin/models/roles/${role}`, { method: 'DELETE' });
  },
  setChoices(choices: ChoiceDraft[]): Promise<ModelsView> {
    return adminApi<ModelsView>('/v1/admin/models/choices', {
      method: 'PUT',
      body: { choices },
    });
  },
  refreshCatalog(): Promise<{ jobId: string }> {
    return adminApi<{ jobId: string }>('/v1/admin/models/catalog/refresh', { method: 'POST' });
  },
};

/** The checks a failed save answered with (422 check_failed), or null. */
export function checksFromError(err: unknown): ModelCheckResult[] | null {
  const body = (err as { body?: { error?: unknown; checks?: unknown } }).body;
  return body?.error === 'check_failed' && Array.isArray(body.checks)
    ? (body.checks as ModelCheckResult[])
    : null;
}

// ----- Wording -------------------------------------------------------------------

export function providerName(p: ModelProvider): string {
  return p === 'anthropic' ? 'Anthropic' : 'OpenRouter';
}

/**
 * Which route a model is reached by. Claude is in the catalog twice, from
 * Anthropic directly and through OpenRouter, and the two look alike.
 */
export function viaText(m: Pick<ModelView, 'provider'>): string {
  return `via ${providerName(m.provider)}`;
}

/** Where a role's model came from, in the page's words. */
export function sourceText(
  r: Pick<ModelRoleView, 'source' | 'envVar' | 'follows'>,
  roles: ModelRoleView[]
): string {
  switch (r.source) {
    case 'admin':
      return 'Set here';
    case 'deploy_variable':
      return `Deploy variable ${r.envVar ?? ''}`.trim();
    case 'built_in':
      return r.envVar !== null ? `Built-in default (${r.envVar} is not set)` : 'Built-in default';
    case 'follows': {
      const leader = roles.find((x) => x.role === r.follows);
      return `Follows ${leader?.label ?? 'another role'}`;
    }
  }
}

/** The reset button: it names the model the role goes back to. */
export function resetText(
  r: Pick<ModelRoleView, 'follows' | 'fallback'>,
  roles: Pick<ModelRoleView, 'role' | 'label'>[]
): string {
  if (r.fallback.source === 'follows') {
    const leader = roles.find((x) => x.role === r.follows);
    return `Follow ${leader?.label ?? 'the default'} again`;
  }
  const models = r.fallback.models;
  if (models.length === 1) return `Reset to ${models[0]!.label}`;
  return r.fallback.source === 'built_in'
    ? 'Reset to the built-in list'
    : 'Reset to the deploy variable';
}

/**
 * What a role set here would run on without that choice, in a line:
 * "Built-in default: Claude Haiku 4.5".
 */
export function fallbackText(
  r: Pick<ModelRoleView, 'follows' | 'fallback' | 'envVar'>,
  roles: Pick<ModelRoleView, 'role' | 'label'>[]
): string {
  const names = r.fallback.models.map((m) => `${m.label} (${viaText(m)})`).join(', ');
  switch (r.fallback.source) {
    case 'built_in':
      return `Built-in default: ${names}`;
    case 'deploy_variable':
      return `Deploy variable ${r.envVar ?? ''}: ${names}`;
    case 'follows': {
      const leader = roles.find((x) => x.role === r.follows);
      return `Without a choice here: follows ${leader?.label ?? 'another role'} (${names})`;
    }
  }
}

/** "$4", "$2.50", "$0.10", "$0.125": at least two decimals below $1 or for a fraction, up to four. */
export function dollars(n: number): string {
  if (n === 0) return '$0';
  if (Number.isInteger(n)) return `$${n}`;
  const fixed = n.toFixed(4).replace(/0{1,2}$/, '');
  return `$${fixed.replace(/(\.\d\d\d)0$/, '$1')}`;
}

/** "$4 in · $20 out per MTok", with the long-prompt set when there is one. */
export function priceText(m: Pick<ModelView, 'prices' | 'priceSource'>): string {
  if (m.prices === null) return 'No price yet';
  const base = `${dollars(m.prices.input)} in · ${dollars(m.prices.output)} out`;
  const long = m.prices.longPrompt;
  const tier =
    long !== null
      ? ` (over ${Math.round(long.overPromptTokens / 1000)}K prompt tokens: ${dollars(long.input)} · ${dollars(long.output)})`
      : '';
  return `${base} per MTok${tier}`;
}

/**
 * A model on Neo's pictures is billed by the picture (TOM-641): the per-token
 * rates in the catalog are its text rates, or $0 for a model that only draws.
 * The check draws one picture and shows what it cost.
 */
export const PICTURE_PRICE_TEXT = 'Billed by the picture';
export const PICTURE_PRICE_HINT = 'Check draws one and shows what it cost';

/**
 * A model whose calls carry no privacy rule (TOM-657, ADR 0389): the chip on
 * its row and in the picker, and the sentence under it. Only the picture's
 * description is ever sent to one.
 */
export const NO_PRIVACY_RULE_TEXT = 'No privacy rule';
export const NO_PRIVACY_RULE_DETAIL =
  "No privacy rule: the provider may keep requests and use them under its own terms. It is sent only the picture's description, never the boat's data.";

/** A check step in a line: "Picture ok in 9.5 s, $0.0672". */
export function checkStepText(s: ModelCheckStep): string {
  const cost = s.costUsd != null ? `, ${dollars(Number(s.costUsd.toFixed(4)))}` : '';
  return `${checkStepName(s.name)} ${s.ok ? 'ok' : 'failed'} in ${(s.latencyMs / 1000).toFixed(1)} s${cost}`;
}

/** Where a price comes from, in a few words. */
export function priceSourceText(m: Pick<ModelView, 'priceSource'>): string | null {
  switch (m.priceSource) {
    case 'anthropic':
      return "Anthropic's price list";
    case 'openrouter':
      return "OpenRouter's catalog";
    case 'built_in':
      return 'the built-in table';
    case 'ceiling':
      return 'not priced yet: counted at the highest Claude price';
    default:
      return null;
  }
}

const CHECK_NAMES: Record<ModelCheckStep['name'], string> = {
  tools: 'Tool calls',
  structured_output: 'JSON replies',
  text: 'Plain reply',
  image_output: 'Picture',
};

export function checkStepName(name: ModelCheckStep['name']): string {
  return CHECK_NAMES[name];
}

/** A check's result in a sentence. */
export function checkSummary(r: ModelCheckResult): string {
  if (r.refused === 'invalid_model')
    return `${r.model} is not a model id (anthropic:claude-… or openrouter:vendor/model).`;
  if (r.refused === 'not_configured') return `${r.model} has no API key for its provider here.`;
  if (r.ok)
    return `${r.model} passed: ${r.steps.map((s) => checkStepName(s.name).toLowerCase()).join(', ')}.`;
  const failed = r.steps.filter((s) => !s.ok);
  return `${r.model} failed: ${failed.map((s) => `${checkStepName(s.name).toLowerCase()} (${s.detail ?? 'no answer'})`).join('; ')}.`;
}

/** A /model alias suggested for a catalog model: its last id segment, shortened. */
export function suggestAlias(m: Pick<ModelView, 'modelId'>, taken: string[]): string {
  const last = m.modelId.split('/').pop() ?? m.modelId;
  const base = last
    .replace(/^claude-/, '')
    .replace(/-\d{8}$/, '')
    .toLowerCase()
    .replace(/[^a-z0-9.-]/g, '-')
    .slice(0, 20)
    .replace(/^[^a-z0-9]+/, '');
  const root = base.length > 0 ? base : 'model';
  if (!taken.includes(root)) return root;
  for (let i = 2; i < 100; i += 1) {
    const next = `${root.slice(0, 20)}-${i}`;
    if (!taken.includes(next)) return next;
  }
  return root;
}

/** Whether a model can see photos and PDFs, from the catalog, for a new /model choice. */
export function seesAttachments(m: Pick<ModelView, 'capabilities'>): boolean {
  return m.capabilities?.imageInput === true && m.capabilities?.pdfInput === true;
}

/** The /model list as the page edits it. */
export function choiceDrafts(choices: ModelChoiceView[]): ChoiceDraft[] {
  return choices.map((c) => ({
    alias: c.alias,
    model: c.model.id,
    label: c.label,
    graphics: c.graphics,
    vision: c.vision,
  }));
}

export function sameChoices(a: ChoiceDraft[], b: ChoiceDraft[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// ----- The catalog view ------------------------------------------------------------

/** "1M", "200K", "131K", "4,096": a token count, short. */
export function tokens(n: number): string {
  // One decimal at most, and none when it is .0: 1,048,576 is "1M".
  if (n >= 1_000_000) return `${Number((n / 1_000_000).toFixed(1))}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}K`;
  return n.toLocaleString('en-US');
}

const CAPABILITY_LABELS: ReadonlyArray<[string, string]> = [
  ['tools', 'Tools'],
  ['structuredOutputs', 'JSON'],
  ['imageInput', 'Photos'],
  ['pdfInput', 'PDFs'],
  ['adaptiveThinking', 'Thinking'],
  ['imageOutput', 'Draws pictures'],
];

/** What a model can do, as the catalog view's chips. */
export function capabilityLabels(m: Pick<ModelView, 'capabilities'>): string[] {
  return CAPABILITY_LABELS.filter(([key]) => m.capabilities?.[key] === true).map(
    ([, label]) => label
  );
}

export interface CatalogFilter {
  q: string;
  provider: 'all' | ModelProvider;
  showDelisted: boolean;
}

/** The catalog view's rows: matching the search in the name or the id, by provider. */
export function filterCatalog(models: ModelView[], f: CatalogFilter): ModelView[] {
  const words = f.q
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 0);
  return models.filter((m) => {
    if (f.provider !== 'all' && m.provider !== f.provider) return false;
    if (!f.showDelisted && m.listing === 'delisted') return false;
    const text = `${m.label} ${m.id}`.toLowerCase();
    return words.every((w) => text.includes(w));
  });
}

/** The line /admin/models shows when Neo's board editor cannot see its pages (cloud ADR 0393 §12.3, TOM-665). */
export const PAGE_PICTURES_UNSEEN_TEXT = 'Neo will not see the pictures of the pages it writes';

/**
 * The board editor's model when it cannot see the picture of each page it
 * writes (cloud ADR 0393 §12.3, Q19): the first model of the `neo_boards`
 * role, or of the chat's while that role is unset, when it is not an Anthropic
 * model that takes images. Other providers take no image in a tool's result.
 * Null when it sees them, or the page cannot tell which model it is.
 */
export function pagePicturesUnseenBy(view: Pick<ModelsView, 'roles'>): ModelView | null {
  const role = (name: ModelRole) => view.roles.find((r) => r.role === name);
  const effective = (r: ModelRoleView | undefined): ModelView | null =>
    r === undefined ? null : (r.models[0] ?? r.fallback.models[0] ?? null);
  const boards = role('neo_boards');
  const model =
    boards !== undefined && (boards.models.length > 0 || boards.follows === null)
      ? effective(boards)
      : effective(role('neo_chat'));
  if (model === null) return null;
  const seesImages = model.provider === 'anthropic' && model.capabilities?.imageInput !== false;
  return seesImages ? null : model;
}
